import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmDialog } from '../../../components'
import { deleteStoreOverride, fetchStoreOverrides, updateWarehouseConfigurations, upsertStoreOverride } from '../../../services/warehouseConfigurationService'
import { formatDate } from '../../CubboLastmile/utils'
import { PACKING_AUDIT_KEY, mapConfigError, normalizeOverrides, parsePercent, summarize, withOverrideRow, withoutOverrideRow } from './packingAuditConfig'
import { retryTransient } from '../../../helpers/retryTransient'
import { StoreExceptionModal } from './StoreExceptionModal'

const T = 'packing_config.audit'
const COUNTERS = ['following', 'exceptions', 'excluded']
const inputClasses = 'shadow-sm block w-full sm:text-sm rounded-md border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500'

// Layout A: the warehouse number, three counters, and the stores that have their own value.
export const PackingAuditCard = ({ warehouseId, warehouse, config, onNotify }) => {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(String(config.value))
  // The payload lives apart from `open` (same pattern as `modal` below), so closing the dialog
  // never drops the value/row a stale-closure click would need, and so the dialogs' own
  // `onConfirm` can refuse to fire once `open` is false.
  // Success closes with `open: false` only (the payload — value/previous/row — survives the
  // close), so the fading dialog keeps showing its own title/copy instead of flipping to the next
  // draft's. `previous` is captured at open time since `config.value` is already overwritten by
  // the save's own setQueryData seed by the time the dialog quotes it.
  const [saveDialog, setSaveDialog] = useState({ open: false, value: null, previous: null, error: null })
  const [modal, setModal] = useState({ open: false, store: null, value: undefined })
  const [removeDialog, setRemoveDialog] = useState({ open: false, row: null, error: null })

  useEffect(() => setDraft(String(config.value)), [config.value])

  const overridesKey = ['warehouse_store_overrides', warehouseId, PACKING_AUDIT_KEY]
  const overridesQuery = useQuery(overridesKey, () => fetchStoreOverrides(warehouseId, PACKING_AUDIT_KEY), {
    refetchOnWindowFocus: false,
    retry: retryTransient,
  })
  const overrides = normalizeOverrides(overridesQuery.data)
  // The confirmation copy and the add modal's exclusions depend on this list: nothing that
  // writes is offered before it has loaded once.
  const overridesReady = overridesQuery.data !== undefined
  const counts = summarize(overridesQuery.data?.stores_count ?? 0, overrides)

  // A GET already in flight when a write lands must not be allowed to resolve afterwards and
  // clobber the write's setQueryData seed (the warehouse config, or the exceptions list after an
  // add/edit/remove) with data from before the write. react-query 3.39.3's
  // parseFilterArgs(arg1, arg2, arg3) reads options from arg3 once arg1 is a query key —
  // {cancelRefetch:true} as arg2 is swallowed as a (no-op) filter override, not read as an
  // option — so cancelRefetch has to go in the third slot.
  // Every write refreshes first and seeds second: setQueryData clears the query's isFetching
  // flag, after which cancelRefetch no longer sees the stale GET and its late response lands anyway.
  // Each write invalidates only the query it actually changed: the warehouse save never touches
  // the overrides list, and an exception add/edit/remove never touches the warehouse config.
  const refreshWarehouse = () => queryClient.invalidateQueries(['warehouse_configurations', warehouseId], undefined, { cancelRefetch: true })
  // A second exception write inside one GET round trip can't rely on the flag either: the first
  // write's seed already cleared it while that write's refetch is still running. So whatever overrides
  // fetch is current is cancelled first, silently (a non-silent cancel would revert the seed).
  const refreshOverrides = () => {
    queryClient.cancelQueries(overridesKey, undefined, { silent: true })
    return queryClient.invalidateQueries(overridesKey, undefined, { cancelRefetch: true })
  }
  // Nothing to seed before the list has loaded once (react-query 3 would store an undefined result).
  const seedOverrides = update => {
    if (queryClient.getQueryData(overridesKey) !== undefined) queryClient.setQueryData(overridesKey, update)
  }
  const when = iso => formatDate(iso, i18n.language, warehouse?.time_zone)

  const saveWarehouse = useMutation(value => updateWarehouseConfigurations(warehouseId, { [PACKING_AUDIT_KEY]: value }), {
    onSuccess: data => {
      refreshWarehouse()
      // Seed the cache from the response (GET shape) so every copy that quotes the warehouse value is
      // right before the refetch lands (ReceivingSettingsContainer does the same).
      queryClient.setQueryData(['warehouse_configurations', warehouseId], data)
      const saved = (data?.configurations || []).find(c => c.key === PACKING_AUDIT_KEY)
      // Re-seed from the response: an unchanged config.value would not re-run the effect above.
      if (saved) setDraft(String(saved.value))
      // Keep the payload so the fading dialog still shows its own copy.
      setSaveDialog(current => ({ ...current, open: false }))
      onNotify(t(`${T}.saved`))
    },
    // The dialog stays open and shows the mapped error inside itself, instead of closing into a
    // card-level banner that can sit above the viewport.
    onError: error => {
      setSaveDialog(current => ({ ...current, error: t(mapConfigError(error)) }))
    },
  })

  const removeException = useMutation(storeId => deleteStoreOverride(warehouseId, PACKING_AUDIT_KEY, storeId), {
    // The DELETE answers 204 with no body, so the row is dropped from the cached list right away: it
    // is gone before the toast, and the dialog never waits on the refetch (whose retries, or a hung
    // GET, would hold "Quitando" with Cancelar locked). The refetch runs in the background.
    onSuccess: (_data, storeId) => {
      refreshOverrides()
      seedOverrides(old => withoutOverrideRow(old, storeId))
      // Keep the payload so the fading dialog still names its own store.
      setRemoveDialog(current => ({ ...current, open: false }))
      onNotify(t(`${T}.exception_removed`))
    },
    onError: error => {
      setRemoveDialog(current => ({ ...current, error: t(mapConfigError(error, 'remove_generic')) }))
    },
  })

  // The PUT answers with the saved row (GET shape), which seeds the cached list: the row is listed
  // before the toast, and a reopen right away no longer offers the store. The modal closes when this
  // resolves, so the refetch runs in the background instead of holding "Guardando..." through its
  // retries or a hung GET.
  const submitException = async (store, value) => {
    const row = await upsertStoreOverride(warehouseId, PACKING_AUDIT_KEY, store.id, value)
    refreshOverrides()
    if (row?.store_id != null) seedOverrides(old => withOverrideRow(old, row))
    onNotify(t(modal.value === undefined ? `${T}.exception_added` : `${T}.exception_updated`))
  }

  const parsed = parsePercent(draft)
  const changed = parsed.value !== undefined && parsed.value !== config.value
  const confirmParams = {
    warehouse: warehouse?.name || t(`${T}.confirm.this_warehouse`),
    value: saveDialog.value,
    // `config.value` is already the new value by the time the success seed lands, so the fading
    // dialog must quote the `previous` captured when it was opened instead.
    previous: saveDialog.previous,
    following: counts.following,
  }
  // The counters' own `counts` (not `overrides.length`, which disagrees with "Con excepción"
  // because that counter excludes 0% stores) drive the own-percentage sentence, omitted entirely
  // when nothing keeps its own value.
  const ownValueParts = [
    counts.exceptions > 0 && t(`${T}.confirm.own_exceptions`, { n: counts.exceptions }),
    counts.excluded > 0 && t(`${T}.confirm.own_excluded`, { n: counts.excluded }),
  ].filter(Boolean)
  const ownValueText = ownValueParts.length > 0 ? t(`${T}.confirm.own_value`, { parts: ownValueParts.join(', ') }) : null
  const pendingNoteText = saveDialog.value === 0 ? t(`${T}.confirm.pending_note`) : null

  return (
    <section className="bg-white border border-gray-200 rounded-md p-5 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2">{t(`${T}.title`)}</h3>
        <p className="text-sm text-gray-600">{t(`${T}.explainer`)}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="warehouse-audit-percentage">
            {t(`${T}.warehouse_label`)}
          </label>
          <div className="flex items-center gap-2">
            <div className="relative w-28">
              <input
                id="warehouse-audit-percentage"
                type="text"
                inputMode="numeric"
                className={`${inputClasses} pr-8 text-right`}
                value={draft}
                onChange={e => setDraft(e.target.value)}
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">%</span>
            </div>
            <Button
              type="primary"
              disabled={!changed || !overridesReady}
              onClick={() => setSaveDialog({ open: true, value: parsed.value, previous: config.value, error: null })}
            >
              {t(`${T}.save`)}
            </Button>
            {draft !== String(config.value) && (
              <button type="button" className="text-sm text-gray-500 hover:underline" onClick={() => setDraft(String(config.value))}>
                {t(`${T}.discard`)}
              </button>
            )}
          </div>
          {parsed.error && <p className="mt-1 text-xs text-red-600">{t(`${T}.errors.${parsed.error}`)}</p>}
          <p className="mt-1 text-xs text-gray-500">
            {config.is_default ? t(`${T}.unset`) : t(`${T}.stamp`, { name: config.updated_by?.name || '—', date: when(config.updated_at) })}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {COUNTERS.map(counter => (
            <div key={counter} className="rounded-md border border-gray-200 px-3 py-2">
              <p className="text-xs text-gray-500">{t(`${T}.counters.${counter}`)}</p>
              <p className="text-lg font-semibold text-gray-900" data-testid={`counter-${counter}`}>
                {overridesReady ? counts[counter] : '—'}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">{t(`${T}.exceptions_title`)}</h4>
            <p className="text-xs text-gray-500">{t(`${T}.exceptions_hint`)}</p>
          </div>
          <Button type="secondary" disabled={!overridesReady} onClick={() => setModal({ open: true, store: null, value: undefined })}>
            {t(`${T}.add_exception`)}
          </Button>
        </div>
        {overridesQuery.isError && (
          <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 mb-2 flex items-center justify-between">
            <span>{t(`${T}.exceptions_load_error`)}</span>
            <Button type="secondary" onClick={() => overridesQuery.refetch()}>
              {t('packing_config.retry')}
            </Button>
          </div>
        )}
        {!overridesReady ? null : overrides.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-md">{t(`${T}.exceptions_empty`)}</p>
        ) : (
          <div className="border border-gray-200 rounded-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(`${T}.columns.store`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(`${T}.columns.audit`)}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t(`${T}.columns.updated`)}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {overrides.map(row => (
                  <tr key={row.store_id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.store_name}</td>
                    <td className="px-4 py-2">
                      {row.value === 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {t(`${T}.excluded_pill`)}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{row.value}%</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {row.updated_by?.name || '—'} · {when(row.updated_at)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="text-sm text-indigo-600 hover:underline mr-4"
                        onClick={() => setModal({ open: true, store: { id: row.store_id, name: row.store_name }, value: row.value })}
                      >
                        {t(`${T}.edit`)}
                      </button>
                      <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => setRemoveDialog({ open: true, row, error: null })}>
                        {t(`${T}.remove`)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={saveDialog.open}
        // Closing only flips `open`. The payload stays (so a stale click still reads the dialog's
        // own state instead of a nulled-out one), and so does `error` — clearing it here would
        // make the error line vanish mid-fade; the open handler above already starts every open
        // with a fresh `error: null`, so nothing leaks.
        setOpen={open => setSaveDialog(current => ({ ...current, open }))}
        title={t(`${T}.confirm.title`, confirmParams)}
        description={
          <>
            {t(saveDialog.value === 0 ? `${T}.confirm.description_off` : `${T}.confirm.description`, confirmParams)}
            {ownValueText && ` ${ownValueText}`}
            {pendingNoteText && ` ${pendingNoteText}`}
            {saveDialog.error && (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {saveDialog.error}
              </p>
            )}
          </>
        }
        confirmLabel={t(`${T}.confirm.confirm`)}
        cancelLabel={t(`${T}.confirm.cancel`)}
        loading={saveWarehouse.isLoading}
        loadingLabel={t(`${T}.confirm.loading`)}
        type={saveDialog.value === 0 ? 'danger' : 'default'}
        onConfirm={() => {
          // A stale click during the leave transition must not fire.
          if (!saveDialog.open) return
          setSaveDialog(current => ({ ...current, error: null }))
          saveWarehouse.mutate(saveDialog.value)
        }}
      />
      <StoreExceptionModal
        open={modal.open}
        setOpen={open => setModal(current => ({ ...current, open }))}
        warehouseId={warehouseId}
        initialStore={modal.store}
        initialValue={modal.value}
        excludedStoreIds={overrides.map(row => row.store_id)}
        warehouseValue={config.value}
        onSubmit={submitException}
      />
      <ConfirmDialog
        open={removeDialog.open}
        // Same as the save dialog above — closing never clears `error`.
        setOpen={open => setRemoveDialog(current => ({ ...current, open }))}
        title={t(`${T}.remove_confirm.title`, { store: removeDialog.row?.store_name })}
        description={
          <>
            {t(`${T}.remove_confirm.description`, { value: config.value })}
            {config.value === 0 && ` ${t(`${T}.remove_confirm.warehouse_off`)}`}
            {removeDialog.error && (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {removeDialog.error}
              </p>
            )}
          </>
        }
        confirmLabel={t(`${T}.remove_confirm.confirm`)}
        cancelLabel={t(`${T}.remove_confirm.cancel`)}
        loading={removeException.isLoading}
        loadingLabel={t(`${T}.remove_confirm.loading`)}
        type="danger"
        onConfirm={() => {
          if (!removeDialog.open) return
          setRemoveDialog(current => ({ ...current, error: null }))
          removeException.mutate(removeDialog.row.store_id)
        }}
      />
    </section>
  )
}
